import { Visibility } from '@mui/icons-material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { List, ListItem } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { MdAdd } from 'react-icons/md';
import { Network } from '../../../../../interfaces/interfaces';
import { NetworkComponentInterface } from '../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import VyTooltip from '../../../../common/VyTooltip';
import { PageBuilderSettingsSection, PageBuilderSettingsTable } from '../PageBuilderSettingsTable';
import { AddNetworkRoomForm } from './AddNetworkRoomForm';
import NetworkPopoverSettings from './NetworkPopoverSettings';

interface NetworkSettingsTableInterface {
  component: NetworkComponentInterface;
  onChange: (c: NetworkComponentInterface) => void;
}

const NetworkSettingsTable = ({ component, onChange }: NetworkSettingsTableInterface) => {
  const { t } = useTranslation(['pagebuilder']);

  const networks = useSelector((state) => state.common.networks);

  const [showAddForm, setShowAddForm] = useState(networks?.length === 0);

  function orderComponentNetworkingRooms() {
    let biggestIndex = -1;
    return [...networks].sort(sortNetworks).map((network) => {
      const existingNetwork = component.networks?.find((n) => n.networkid === network?._id);
      if (existingNetwork) {
        if (existingNetwork.sortIndex > biggestIndex) biggestIndex = existingNetwork.sortIndex;
        return existingNetwork;
      }
      biggestIndex++;
      return {
        networkid: network._id,
        sortIndex: biggestIndex,
        visible: false,
      };
    });
  }

  function sortNetworks(a: Network, b: Network) {
    const valueA = component?.networks?.find((n) => n.networkid === a._id)?.sortIndex ?? networks.length + 1;
    const valueB = component?.networks?.find((n) => n.networkid === b._id)?.sortIndex ?? networks.length + 1;
    if (valueA < valueB) {
      return -1;
    }
    if (valueA > valueB) {
      return 1;
    }
    return 0;
  }

  function handleSortNetwork(networkid?: string, sortUp?: boolean) {
    let nets: {
      networkid: string;
      sortIndex: number;
      visible: boolean;
    }[] = [];
    // necessary to lose inside object references (doesn't work with copying array)
    orderComponentNetworkingRooms().map((network) => {
      nets?.push({ networkid: network.networkid, sortIndex: network.sortIndex, visible: network.visible });
    });
    nets = nets.sort((a, b) => a.sortIndex - b.sortIndex);
    if (nets) {
      const networkIndex = nets?.findIndex((n) => n.networkid === networkid);
      const currSortIndex = nets[networkIndex].sortIndex;
      if (sortUp) {
        nets[networkIndex].sortIndex = nets[networkIndex - 1].sortIndex;
        nets[networkIndex - 1].sortIndex = currSortIndex;
      } else {
        nets[networkIndex].sortIndex = nets[networkIndex + 1].sortIndex;
        nets[networkIndex + 1].sortIndex = currSortIndex;
      }
      onChange({ ...component, networks: nets });
    }
  }

  function handleSelectNetwork(networkId: string, isVisible: boolean) {
    let _networks = orderComponentNetworkingRooms();
    _networks = _networks.map((network) => {
      if (network.networkid === networkId) return { ...network, visible: isVisible };
      return network;
    });
    onChange({ ...component, networks: _networks });
  }

  function handleUnselectNetwork(network?: Network) {
    const nets = component.networks?.slice();
    const networkIndex = nets?.findIndex((n) => n.networkid === network?._id);
    if (networkIndex !== undefined) {
      nets?.splice(networkIndex, 1);
      onChange({ ...component, networks: nets });
    }
  }

  function handleAddNetworkingRoom() {
    setShowAddForm(false);
    onChange({ ...component, networks: orderComponentNetworkingRooms() });
  }

  const settingSections: PageBuilderSettingsSection[] = [
    {
      name: 'Komponente',
      settings: [
        {
          label: t('pagebuilder:tooltip-network-toggle-count'),
          type: 'boolean',
          path: 'showCount',
        },
      ],
    },
    {
      name: t('pagebuilder:networking-select-rooms'),
      actions: [
        {
          icon: (
            <VyTooltip title={t('pagebuilder:networking-add')}>
              <MdAdd />
            </VyTooltip>
          ),
          onClick: () => {
            setShowAddForm(!showAddForm);
          },
        },
      ],
      settings: [
        ...(showAddForm
          ? [
              {
                content: (
                  <List component="div" disablePadding>
                    <AddNetworkRoomForm onComplete={handleAddNetworkingRoom} />
                  </List>
                ),
              },
            ]
          : []),
        {
          label: t('pagebuilder:available-networkingrooms'),
          content: (
            <List component="div" disablePadding>
              {[...networks]?.sort(sortNetworks)?.map((network, i) => {
                const isVisible = component?.networks?.find((n) => n.networkid === network._id)?.visible;
                return (
                  <div key={`av-network-${network._id}`} className="flex flex-row mb-1">
                    <ListItem>
                      <span className="flex-1">{network.name}</span>
                    </ListItem>
                    <VyTooltip placement="top" title={t('pagebuilder:select-network')}>
                      <ListItem
                        style={{ flex: 0 }}
                        button
                        onClick={() => {
                          handleSelectNetwork(network._id, !isVisible);
                        }}>
                        <Visibility color={isVisible ? 'primary' : 'disabled'} />
                      </ListItem>
                    </VyTooltip>
                    <span>
                      <ListItem
                        style={{ flex: 0 }}
                        button
                        disabled={i + 1 === networks.length}
                        onClick={() => {
                          handleSortNetwork(network?._id);
                        }}>
                        <ArrowDownwardIcon />
                      </ListItem>
                    </span>
                    <span>
                      <ListItem
                        style={{ flex: 0 }}
                        button
                        disabled={i === 0}
                        onClick={() => {
                          handleSortNetwork(network?._id, true);
                        }}>
                        <ArrowUpwardIcon />
                      </ListItem>
                    </span>
                    <NetworkPopoverSettings network={network} onDelete={() => handleUnselectNetwork(network)} />
                  </div>
                );
              })}
            </List>
          ),
        },
      ],
    },
  ];

  return (
    <>
      <PageBuilderSettingsTable sections={settingSections} onChange={onChange} component={component} />
    </>
  );
};
export default NetworkSettingsTable;
